import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import TaskBoard from './tabs/TaskBoard';
import { AppStateContext, useSetState, appState } from '../AppState';
import _ from 'lodash';
import { Ticket } from '../shared/interfaces/ticket.interface';
import {
  FILTERS,
  LICENSE_FEATURE,
  FILTERS_STRINGS,
  STATUS_VALUE,
  STATUS_GUID,
  TASK_STATUS_GUID
} from '../shared/utils/constants';
import { getCachedFeature } from '../shared/cache/FeatureCache';
import { platformService } from '../shared/services/platform.service';
import { RenderSkeleton } from './TicketBoardView/RenderSkeleton';
import { CustomViewContext } from './toolbar/CustomViewContextProvider';
import useCustomViewsTickets from './List/useCustomViewsTickets';
import { ListContext } from './List/ListContextProvider';
import ListFunctions from './List/ListFunctions';
import { ListInputItemsEnum, getStatusIdByGuid, getTaskStatusIdByGuid } from './ticketHelper';
import { getViewIdFromStorage } from './toolbar/CustomViewPopup/heplers/customPopupHelper';
import { getCachedUsers } from '../shared/cache/PlatformUserCache';
import {
  LANE_PAGE_SIZE,
  buildLaneFilter,
  combineLaneFilter,
  cpFilterParam,
  selectedViewFilterSignature,
  LanePage,
  refreshWindowParams,
  shouldResetToTop,
  refreshLanePage,
  lookaheadHasMore,
  advanceWindowDown,
  upRequestParams,
  advanceWindowUp,
  dedupeById,
} from './boardWindowing';

export type { LanePage };

const api = new platformService();

const TICKET_EXPAND = `AffectedUsers($select=PlatformUserId),TicketCollaborators($select=PlatformUserId),Tags,TicketLifecycle($expand=Status,Lifecycle($select=Title),Phases($filter=IsCurrent eq true))`;

// The add-ticket dialog drops a stand-in ticket into state while the real one is being
// created, and the board renders it as a skeleton card. It belongs to no lane page, so it
// has to survive our fetches and ride alongside the windowed tickets until it's replaced.
const TEMP_TICKET_TITLE = 'temp';
const isPendingTicket = (ticket: any) => ticket?.Title === TEMP_TICKET_TITLE;

interface PropsI {
  defaultFilters: FilterPropsItems[];
  listFeatureStates: ListFeatureStates;
}

export const Board = ({ defaultFilters, listFeatureStates }: PropsI) => {
  const state = useContext(AppStateContext);
  const setState = useSetState();
  const currentState = appState();
  const currentStateRef = useRef(currentState);
  currentStateRef.current = currentState;

  const [isSlaEnable, setIsSlaEnable] = useState(false);
  const [isLifecycleEnabled, setIsLifecycleEnabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const { listStates, setListStates } = useContext(ListContext);
  const [isDeflectedSelected, setIsDeflectedSelected] = useState(false);
  const [lanePageState, setLanePageState] = useState<Record<string, LanePage>>({});
  const lanePageStateRef = useRef(lanePageState);
  lanePageStateRef.current = lanePageState;

  const [allTickets, setAllTickets] = useState({
    active: [] as Ticket[],
    closed: [] as Ticket[],
    all: [] as Ticket[],
  });
  const [parentFilteredTickets, setParentFilteredTickets] = useState([] as Ticket[]);
  const [arrangedTickets, setArrangedTickets] = useState([] as Ticket[]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fetchSeqRef = useRef(0);

  const slasRef = useRef<any[]>([]);
  const tasksRef = useRef<any[]>([]);
  const approvalsRef = useRef<any[]>([]);
  const mergedTicketsRef = useRef<any[]>([]);
  const completedStatusIdRef = useRef<number | null>(null);

  useCustomViewsTickets({ defaultFilters, isBoardView: true, setParentFilteredTickets, allTickets });

  const { setTicketInterval, getFilterExpressions } = ListFunctions();
  const { customViewStates } = useContext(CustomViewContext);

  // The per-lane fetch has to carry the same server predicate the list sends (custom view,
  // child filter, default view, custom properties) or a lane only ever filters its newest
  // ~20 rows. These come from a fresh render each time, so ref them for the silent-refresh
  // interval and the []-deps fetch callbacks below.
  const getFilterExpressionsRef = useRef(getFilterExpressions);
  getFilterExpressionsRef.current = getFilterExpressions;
  const customViewStatesRef = useRef(customViewStates);
  customViewStatesRef.current = customViewStates;
  const defaultFiltersRef = useRef(defaultFilters);
  defaultFiltersRef.current = defaultFilters;

  const resolveViewPredicate = () =>
    getFilterExpressionsRef.current(customViewStatesRef.current, undefined, defaultFiltersRef.current, true);

  // getFilterExpressions throws while the selected default-view id is transient or out of range
  // (`parentDefaultFilter[defaultViewId]` is undefined — the list's own fetch catches the same
  // case, see ListFunctions.getpaginatedTickets). Fall back to an unfiltered lane fetch rather
  // than failing the whole board; the refetch effect that follows the transient carries the real
  // predicate.
  const resolveViewPredicateSafely = async (): Promise<{ main: string; custom: string }> => {
    try {
      return await resolveViewPredicate();
    } catch (err) {
      console.warn('Board: view predicate unavailable, fetching lanes unfiltered', err);
      return { main: '', custom: '' };
    }
  };

  // The predicate the current lane windows were fetched with. fetchMore/fetchPrevious read it
  // instead of re-resolving (an uncached /me round-trip per scroll) so every page of a lane pages
  // the same filtered set; getTicketsWithDetails refreshes it, so a date-relative filter rolling
  // over is picked up on the next full refetch (view change or the silent poll).
  const viewPredicateRef = useRef<{ main: string; custom: string }>({ main: '', custom: '' });

  const getLaneKeys = (boardFilter: number): string[] => {
    const cs = currentStateRef.current;
    switch (boardFilter) {
      case 1: return ['no_category', ...(cs.categories ?? []).map((c: any) => `category_${c.Id}`)];
      case 2: return (cs.priority ?? []).map((p: any) => `priority_${p.Id}`);
      case 3: return ['no_group', ...(cs.supportgroups ?? []).map((g: any) => `group_${g.Id}`)];
      case 4: return ['no_team', ...(cs.teams ?? []).map((t: any) => `team_${t.Id}`)];
      case 0:
      default: return (cs.ticketStatus ?? []).map((s: any) => `status_${s.Id}`);
    }
  };

  const getTicketLaneKey = (ticket: any, boardFilter: number): string => {
    const cs = currentStateRef.current;
    switch (boardFilter) {
      case 1: return ticket.CategoryId == null ? 'no_category' : `category_${ticket.CategoryId}`;
      case 2: return `priority_${ticket.PriorityId}`;
      case 3: return !ticket.SupportGroupId ? 'no_group' : `group_${ticket.SupportGroupId}`;
      case 4: return ticket.TeamId == null ? 'no_team' : `team_${ticket.TeamId}`;
      case 0:
      default: {
        const defaultId = (cs.ticketStatus ?? []).find((s: any) => s.IsDefault)?.Id ?? cs.ticketStatus?.[0]?.Id;
        return `status_${ticket.StatusId ?? defaultId}`;
      }
    }
  };

  const enrichTicket = (ticket: any): Ticket => {
    const tasks = tasksRef.current;
    const slas = slasRef.current;
    const approvals = approvalsRef.current;
    const merged = mergedTicketsRef.current;
    const completedStatusId = completedStatusIdRef.current;

    const ticketTasks = tasks.filter((t: any) => t.TicketId === ticket.Id);
    const ticketTaskWithStatus = ticketTasks.map((task: any) => ({
      ...task,
      Status: currentStateRef.current?.taskStatus?.find((s: any) => s.Id === task.StatusId),
    }));
    const ticketApprovals = approvals.filter((a: any) => a.TicketId === ticket.Id);

    return {
      ...ticket,
      TicketSlas: slas.filter((sla: any) => sla.TicketId === ticket.Id),
      TicketTasks: ticketTaskWithStatus,
      HasActiveTasks: ticketTaskWithStatus.some((t: any) => t.StatusId !== completedStatusId),
      PendingTicketApprovals: ticketApprovals,
      HasPendingApprovals: ticketApprovals.length > 0,
      SourceTicketId: merged.find((m: any) => m.RelatedTicketId === ticket.Id)?.SourceTicketId ?? null,
    };
  };

  const hydrateUsers = async (raw: any[]) => {
    const userIds = raw.flatMap((t: any) => [
      t.RequesterId, t.AssigneeId,
      ...(t.AffectedUsers ?? []).map((a: any) => a.PlatformUserId),
      ...(t.TicketCollaborators ?? []).map((c: any) => c.PlatformUserId),
    ].filter(Boolean));
    const users = await getCachedUsers(userIds);
    raw.forEach((t: any) => {
      t.Requester = users.find((u: any) => u.Id === t.RequesterId);
      t.Assignee = users.find((u: any) => u.Id === t.AssigneeId);
      t.AffectedUsers?.forEach((a: any) => { a.PlatformUser = users.find((u: any) => u.Id === a.PlatformUserId); });
      t.TicketCollaborators?.forEach((c: any) => { c.PlatformUser = users.find((u: any) => u.Id === c.PlatformUserId); });
    });
  };

  const checkSlaFeatureHasBeenEnabled = async () => {
    try {
      const enabled = await getCachedFeature(LICENSE_FEATURE.ServiceLevelAgreements);
      await getCachedFeature(LICENSE_FEATURE.MultiDepartment);
      setIsSlaEnable(enabled);
    } catch (error) {
      console.error(error);
    }
  };

  const getTicketsWithDetails = async (silent = false) => {
    const seq = ++fetchSeqRef.current;
    // -100 is the transient listFilter the TableMenu default-view switch sets before the real id
    // lands on the next tick; the view predicate can't resolve yet and the id change re-triggers
    // this. The refetch effect guards it too, but mount / refresh / boardFilter don't.
    if (currentStateRef.current.listFilter === -100) return;
    if (!silent) {
      setIsLoading(true);
      setListStates(prev => ({ ...prev, isTicketsFetching: true, errorDuringFetch: false }));
    }
    try {
      // Resolve the view predicate alongside the feature/SLA fetches rather than after them.
      const viewPredicatePromise = resolveViewPredicateSafely();

      const [isRelateTicketEnabled, isTicketTaskEnabled, isEnabledLifecycle] = await Promise.all([
        getCachedFeature(LICENSE_FEATURE.RelatedTickets),
        getCachedFeature(LICENSE_FEATURE.TicketTasks),
        getCachedFeature(LICENSE_FEATURE.TicketLifecycle),
      ]);

      const [slas, ticketTask, mergedTickets, pendingApprovals] = await Promise.all([
        api.getTicketSla(FILTERS_STRINGS.TICKET_SLA_FILTER),
        isTicketTaskEnabled ? api.getTicketTasks(`?$filter=IsDeleted%20eq%20false`) : Promise.resolve([]),
        isRelateTicketEnabled ? api.getAllMergedTickets() : Promise.resolve([]),
        api.getAllPendingTicketApprovals(),
      ]);

      slasRef.current = slas ?? [];
      tasksRef.current = ticketTask ?? [];
      mergedTicketsRef.current = mergedTickets ?? [];
      approvalsRef.current = pendingApprovals ?? [];
      completedStatusIdRef.current = getTaskStatusIdByGuid(currentStateRef.current.taskStatus, TASK_STATUS_GUID.COMPLETED);

      setIsLifecycleEnabled(isEnabledLifecycle);

      const boardFilter = currentStateRef.current.boardFilter ?? 0;
      const laneKeys = getLaneKeys(boardFilter);
      const { main: viewPredicate, custom: cpFilter } = await viewPredicatePromise;

      const laneResults = await Promise.all(
        laneKeys.map(async (laneKey) => {
          const prev = lanePageStateRef.current[laneKey];
          const filter = combineLaneFilter(buildLaneFilter(laneKey, boardFilter), viewPredicate);

          const fetchWindow = async (windowStart: number, topCount: number) => {
            const result = await api.getTicketList(
              `?$filter=${filter}${cpFilterParam(cpFilter)}&$top=${topCount + 1}&$skip=${windowStart}&$expand=${TICKET_EXPAND}&$orderby=CreatedDate desc`
            );
            const rawAll: any[] = result.data.value ?? [];
            return { rawAll, hasMore: lookaheadHasMore(rawAll.length, topCount), raw: rawAll.slice(0, topCount), windowStart };
          };

          const { windowStart, topCount } = refreshWindowParams(prev, silent);
          let res = await fetchWindow(windowStart, topCount);

          if (shouldResetToTop(silent, windowStart, res.rawAll.length)) {
            res = await fetchWindow(0, LANE_PAGE_SIZE);
          }

          return { laneKey, raw: res.raw, hasMore: res.hasMore, windowStart: res.windowStart };
        })
      );

      const allRaw = laneResults.flatMap(r => r.raw);
      await hydrateUsers(allRaw);

      const active: Ticket[] = [];
      const closed: Ticket[] = [];
      const initialLanePageState: Record<string, LanePage> = {};

      laneResults.forEach(({ laneKey, raw, hasMore, windowStart }) => {
        const enriched = raw.map(enrichTicket);
        enriched.forEach((t: Ticket) => (t.Closed ? closed : active).push(t));
        initialLanePageState[laneKey] = refreshLanePage(windowStart, raw.length, hasMore);
      });

      if (seq === fetchSeqRef.current) {
        // Keep the ref in lockstep with the windows this fetch just built, so a later
        // fetchMore/fetchPrevious extends the same predicate.
        viewPredicateRef.current = { main: viewPredicate, custom: cpFilter };
        setLanePageState(initialLanePageState);
        setAllTickets({ active, closed, all: [...active, ...closed] });

        setState(preState => ({
          ...preState,
          isLoadingSla: false,
          ticketsSla: slas,
          // background refreshes keep any pending card; a full load (mount or the refresh
          // button) starts clean so a placeholder can never get stranded on the board
          tickets: silent ? [...(preState.tickets ?? []).filter(isPendingTicket), ...active] : active,
          closedTickets: closed,
          reloadBoardOnAddTicket: false,
        }));
      }

      if (!silent) setIsLoading(false);
    } catch (error) {
      if (!silent) setIsLoading(false);
      setState(preState => ({
        ...preState,
        isLoadingSla: false,
        ticketsSla: [],
        reloadBoardOnAddTicket: false,
      }));
      console.log(`Error occurred when loading tickets with SLAs and Tasks:`, error);
    }
    if (!silent) setListStates(prev => ({ ...prev, isTicketsFetching: false }));
  };

  const fetchMoreForLane = useCallback(async (laneKey: string) => {
    const page = lanePageStateRef.current[laneKey];
    if (!page || page.isLoading || !page.hasMore) return;

    setLanePageState(prev => ({
      ...prev,
      [laneKey]: { ...prev[laneKey], isLoading: true },
    }));

    try {
      const boardFilter = currentStateRef.current.boardFilter ?? 0;
      const { main: viewPredicate, custom: cpFilter } = viewPredicateRef.current;
      const filter = combineLaneFilter(buildLaneFilter(laneKey, boardFilter), viewPredicate);
      const result = await api.getTicketList(
        `?$filter=${filter}${cpFilterParam(cpFilter)}&$top=${LANE_PAGE_SIZE + 1}&$skip=${page.skip}&$expand=${TICKET_EXPAND}&$orderby=CreatedDate desc`
      );
      const rawAll: any[] = result.data.value ?? [];
      const raw = rawAll.slice(0, LANE_PAGE_SIZE);
      await hydrateUsers(raw);
      const tickets = raw.map(enrichTicket);

      const { dropCount } = advanceWindowDown(page, raw.length, rawAll.length);

      setAllTickets(prev => {
        const newTickets = dedupeById(prev.all, tickets);
        let all = [...prev.all, ...newTickets];
        if (dropCount > 0) {
          const laneItems = all.filter((t: Ticket) => getTicketLaneKey(t, boardFilter) === laneKey);
          const dropIds = new Set(laneItems.slice(0, dropCount).map((t: Ticket) => t.Id));
          all = all.filter((t: Ticket) => !dropIds.has(t.Id));
        }
        return {
          active: all.filter((t: Ticket) => !t.Closed),
          closed: all.filter((t: Ticket) => t.Closed),
          all,
        };
      });

      setLanePageState(prev => ({
        ...prev,
        [laneKey]: advanceWindowDown(prev[laneKey], raw.length, rawAll.length).page,
      }));
    } catch (err) {
      console.error(`Error loading more tickets for lane ${laneKey}:`, err);
      setLanePageState(prev => ({
        ...prev,
        [laneKey]: { ...prev[laneKey], isLoading: false },
      }));
    }
  }, []);

  const fetchPreviousForLane = useCallback(async (laneKey: string) => {
    const page = lanePageStateRef.current[laneKey];
    if (!page || page.isLoadingTop || !page.hasPrevious || page.windowStart <= 0) return;

    setLanePageState(prev => ({
      ...prev,
      [laneKey]: { ...prev[laneKey], isLoadingTop: true },
    }));

    try {
      const boardFilter = currentStateRef.current.boardFilter ?? 0;
      const { main: viewPredicate, custom: cpFilter } = viewPredicateRef.current;
      const filter = combineLaneFilter(buildLaneFilter(laneKey, boardFilter), viewPredicate);
      const { fetchCount, skipStart } = upRequestParams(page);
      const result = await api.getTicketList(
        `?$filter=${filter}${cpFilterParam(cpFilter)}&$top=${fetchCount}&$skip=${skipStart}&$expand=${TICKET_EXPAND}&$orderby=CreatedDate desc`
      );
      const raw: any[] = result.data.value ?? [];
      await hydrateUsers(raw);
      const tickets = raw.map(enrichTicket);

      const { dropBottom } = advanceWindowUp(page, raw.length);

      setAllTickets(prev => {
        const newTickets = dedupeById(prev.all, tickets);
        let all = [...newTickets, ...prev.all];
        if (dropBottom) {
          const laneItems = all.filter((t: Ticket) => getTicketLaneKey(t, boardFilter) === laneKey);
          const dropIds = new Set(laneItems.slice(-LANE_PAGE_SIZE).map((t: Ticket) => t.Id));
          all = all.filter((t: Ticket) => !dropIds.has(t.Id));
        }
        return {
          active: all.filter((t: Ticket) => !t.Closed),
          closed: all.filter((t: Ticket) => t.Closed),
          all,
        };
      });

      setLanePageState(prev => ({
        ...prev,
        [laneKey]: advanceWindowUp(prev[laneKey], raw.length).page,
      }));
    } catch (err) {
      console.error(`Error loading previous tickets for lane ${laneKey}:`, err);
      setLanePageState(prev => ({
        ...prev,
        [laneKey]: { ...prev[laneKey], isLoadingTop: false },
      }));
    }
  }, []);

  useEffect(() => {
    checkSlaFeatureHasBeenEnabled();
    getTicketsWithDetails();
    timerRef.current = setTicketInterval(() => getTicketsWithDetails(true));
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  useEffect(() => {
    if (!listStates.refreshTrigger) return;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setTicketInterval(() => getTicketsWithDetails(true));
    getTicketsWithDetails();
  }, [listStates.refreshTrigger]);

  const didMountBoardFilter = useRef(false);
  useEffect(() => {
    if (!didMountBoardFilter.current) {
      didMountBoardFilter.current = true;
      return;
    }
    setLanePageState({});
    getTicketsWithDetails();
  }, [state.boardFilter]);

  // A view/filter change moves the server predicate, so the lanes must be re-fetched from the
  // top rather than re-sliced in memory: state.listFilter moves on any view switch (custom view
  // -> Math.random(), default view -> its id), state.filtersApplyCount bumps on the child
  // filter, and this signature catches the selected custom view's filters being (re)built —
  // which happens a beat after mount, once defaultFilters is populated, without changing its Id.
  const selectedViewFilterSig = selectedViewFilterSignature(customViewStates.selectedCustomView?.FiltersDropdownValues);

  const didMountViewFilter = useRef(false);
  useEffect(() => {
    if (!didMountViewFilter.current) {
      didMountViewFilter.current = true;
      return;
    }
    if (state.listFilter === -100) return; // transient set by onClickDefaultView, corrected on the next tick
    setLanePageState({});
    getTicketsWithDetails();
  }, [state.listFilter, state.filtersApplyCount, selectedViewFilterSig]);

  useEffect(() => {
    if (!state.reloadBoardOnAddTicket) return;
    setState(preState => ({ ...preState, reloadBoardOnAddTicket: false }));
    // A pending card means the ticket is still being created, so a refetch now would just
    // replay the board without it. The dialog raises this flag again once it has the real
    // ticket, and that pass is the one that can actually pick it up.
    if ((state.tickets ?? []).some(isPendingTicket)) return;
    getTicketsWithDetails(true);
  }, [state.reloadBoardOnAddTicket]);

  useEffect(() => {
    if (parentFilteredTickets && !isLoading) {
      // parentFilteredTickets is already the server-filtered set for the active view — the
      // per-lane fetch applied the predicate. No second in-memory filter pass here.
      setArrangedTickets(parentFilteredTickets);

      if ([state.listFilter, getViewIdFromStorage('default')].includes(ListInputItemsEnum.Deflected)) {
        setIsDeflectedSelected(true);
      } else {
        setIsDeflectedSelected(isDeflectedStatusSelected(state.filterDropdown.selectedValues, customViewStates.selectedCustomView?.FiltersDropdownValues));
      }
    }
  }, [state.filtersApplyCount, parentFilteredTickets]);

  const isDeflectedStatusSelected = (filterDropdownValues: FilterPropsItems[], customViewDropdownValues: FilterPropsItems[]) => {
    const isDeflectedStatus = (dropdownValues: FilterPropsItems[]) => {
      const id = getStatusIdByGuid(state.ticketStatus, STATUS_GUID.DEFLECTED);
      const statusDropdownValues = dropdownValues.find(value => value.title === FILTERS.STATUS.title);
      return statusDropdownValues.values.find(value => value.key === id).selected;
    };
    if (customViewDropdownValues) {
      const isDeflected = isDeflectedStatus(customViewDropdownValues);
      if (isDeflected) return isDeflected;
    }
    return isDeflectedStatus(filterDropdownValues);
  };

  // Pending cards skip the custom-view/child filters on purpose: the ticket that is being
  // created should stay visible until it is saved, whatever the current view is.
  const boardTickets = useMemo(() => {
    const pending = (state.tickets ?? []).filter(isPendingTicket);
    return pending.length > 0 ? [...pending, ...arrangedTickets] : arrangedTickets;
  }, [state.tickets, arrangedTickets]);

  return (
    <>
      {isLoading ? (
        <RenderSkeleton page="board" />
      ) : (
        <TaskBoard
          tickets={boardTickets}
          isDeflectedStatusSelected={isDeflectedSelected}
          boardFilter={state.boardFilter}
          listFilter={state.listFilter}
          appState={state}
          ticketprops={{
            status: state.ticketStatus,
            categories: state.categories,
            priority: state.priority
          }}
          supportgroups={state.supportgroups}
          teams={state.teams}
          isSlaEnabled={isSlaEnable}
          isLifecycleEnabled={isLifecycleEnabled}
          listFeatureStates={listFeatureStates}
          lanePageState={lanePageState}
          onLoadMoreForLane={fetchMoreForLane}
          onLoadPreviousForLane={fetchPreviousForLane}
        />
      )}
    </>
  );
};
